import { useEffect, useMemo, useState } from 'react';
import { useToast } from '../components/ToastContext';
import {
  hostPricingMeta,
  hostListSeasonalPricing,
  hostSaveSeasonalPricing,
  hostDeleteSeasonalPricing,
  hostListCoupons,
  hostSaveCoupon,
  hostDeleteCoupon,
  hostUploadCouponImage,
  hostListOffers,
  hostSaveOffer,
  hostDeleteOffer,
  hostListTaxesFees,
  hostSaveTaxFee,
  hostDeleteTaxFee,
} from '../api/host';
import SeasonalPricingTab from './host/pricing/SeasonalPricingTab';
import CouponsPromoTab from './host/pricing/CouponsPromoTab';
import SpecialOffersTab from './host/pricing/SpecialOffersTab';
import TaxesFeesTab from './host/pricing/TaxesFeesTab';

const TabButton = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full rounded-xl border px-3 py-2 text-left text-sm font-semibold transition ${
      active ? 'bg-emerald-900 text-white border-emerald-700' : 'bg-slate-900 text-slate-100 border-slate-800 hover:bg-slate-800'
    }`}
  >
    {children}
  </button>
);

const HostPricingDeals = ({ accessToken }) => {
  const [active, setActive] = useState('seasonal');
  const [propertyId, setPropertyId] = useState('');
  const [meta, setMeta] = useState({ properties: [] });
  const [loadingMeta, setLoadingMeta] = useState(false);
  const toast = useToast();

  const propertyOptions = useMemo(() => meta.properties || [], [meta]);

  useEffect(() => {
    const load = async () => {
      setLoadingMeta(true);
      try {
        const data = await hostPricingMeta(accessToken);
        setMeta(data || { properties: [] });
        if (!propertyId && data?.properties?.[0]) setPropertyId(data.properties[0].id);
      } catch (err) {
        toast.error(err.message || 'Failed to load properties');
      } finally {
        setLoadingMeta(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-4">
        <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Pricing & Deals</p>
        <h1 className="text-2xl font-bold text-slate-900">Host Pricing & Deals</h1>
        <p className="text-sm text-slate-600">Seasonal rules, coupons, offers, and taxes in one place.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4 space-y-2 shadow-lg">
            <div className="flex items-center justify-between text-slate-200">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-emerald-200 font-semibold">Pricing & Deals</p>
                <p className="text-sm font-semibold">Pricing & Deals</p>
              </div>
              <span className="text-xl text-emerald-400">></span>
            </div>
            <TabButton active={active === 'seasonal'} onClick={() => setActive('seasonal')}>
              Seasonal pricing
            </TabButton>
            <TabButton active={active === 'coupons'} onClick={() => setActive('coupons')}>
              Coupons / Promo codes
            </TabButton>
            <TabButton active={active === 'offers'} onClick={() => setActive('offers')}>
              Special offers
            </TabButton>
            <TabButton active={active === 'taxes'} onClick={() => setActive('taxes')}>
              Taxes & Fees
            </TabButton>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-3">
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 flex flex-wrap gap-3 items-center">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Property</p>
              <select
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                className="mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                disabled={loadingMeta}
              >
                {propertyOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title || `Property ${p.id}`}
                  </option>
                ))}
              </select>
            </div>
            {loadingMeta && <span className="text-xs text-slate-500">Loading properties...</span>}
          </div>

          {active === 'seasonal' && (
            <SeasonalPricingTab
              accessToken={accessToken}
              propertyId={propertyId}
              onRefresh={() => hostListSeasonalPricing(accessToken, propertyId)}
              listFn={hostListSeasonalPricing}
              saveFn={hostSaveSeasonalPricing}
              deleteFn={hostDeleteSeasonalPricing}
            />
          )}
          {active === 'coupons' && (
            <CouponsPromoTab
              accessToken={accessToken}
              propertyId={propertyId}
              properties={propertyOptions}
              listFn={hostListCoupons}
              saveFn={hostSaveCoupon}
              deleteFn={hostDeleteCoupon}
              uploadFn={hostUploadCouponImage}
            />
          )}
          {active === 'offers' && (
            <SpecialOffersTab
              accessToken={accessToken}
              propertyId={propertyId}
              listFn={hostListOffers}
              saveFn={hostSaveOffer}
              deleteFn={hostDeleteOffer}
            />
          )}
          {active === 'taxes' && (
            <TaxesFeesTab
              accessToken={accessToken}
              propertyId={propertyId}
              listFn={hostListTaxesFees}
              saveFn={hostSaveTaxFee}
              deleteFn={hostDeleteTaxFee}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default HostPricingDeals;

